import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import SystemIcon from '../ShortStatus/SystemIcon';
import Button from '../../../components/CustomButtons/ButtonTS';
import CancelIcon from '@material-ui/icons/Cancel';
import Card from '../../../components/Card/CardTS';
import CardBody from '../../../components/Card/CardBody';
import CardHeader from '../../../components/Card/CardHeaderTS';
import CardIcon from '../../../components/Card/CardIcon';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { closeSweetAlertStyle } from '../../helpers/CustomStyles';
import { ISystem } from '../../helpers/interfaces';
import utils from '../../helpers/GlobalVariables';
import { deleteSystem, closeDeleteSystemNotification } from '../../stores/actions/installerActions';
import { getSystems2 } from '../../stores/actions/actions';
import Snackbar from '../../../components/Snackbar/Snackbar';
import Loading from '../Loading/Loading';

interface RemoveSystemConfirmationProps {
	system: ISystem;
	deleteSystemSuccessfully: boolean;
	deleteSystemFailed: boolean;
	mobileView: boolean;
	hideRemoveSystemConfirmation: () => void;
	deleteSystem: (systemId: number) => void;
	closeDeleteSystemNotification: () => void;
	getSystems2: () => void;
}

const closeButtonStyle = {
	border: '1px solid #00acc1',
};

const RemoveSystemConfirmationCard = ({
	system,
	deleteSystemSuccessfully,
	deleteSystemFailed,
	mobileView,
	hideRemoveSystemConfirmation,
	deleteSystem,
	closeDeleteSystemNotification,
	getSystems2,
}: RemoveSystemConfirmationProps) => {
	const [popupTimeoutEnabled, setPopupTimeoutEnabled] = useState(false);
	const [isLoaderRender, setIsLoaderRender] = useState(false);
	useEffect(() => {
		closeDeleteSystemNotification();
	}, [closeDeleteSystemNotification]);

	useEffect(() => {
		if (deleteSystemSuccessfully) {
			setPopupTimeoutEnabled(true);
		}
		if (deleteSystemSuccessfully || deleteSystemFailed) {
			const timeoutNotification = setTimeout(() => {
				closeDeleteSystemNotification();
				setIsLoaderRender(false);
			}, 6000);
			return () => {
				clearTimeout(timeoutNotification);
			};
		}
	}, [closeDeleteSystemNotification, deleteSystemSuccessfully, deleteSystemFailed]);

	useEffect(() => {
		if (popupTimeoutEnabled) {
			const closeWindowTimeout = setTimeout(() => {
				setPopupTimeoutEnabled(false);
				getSystems2();
				hideRemoveSystemConfirmation();
			}, 3000);
			return () => {
				clearTimeout(closeWindowTimeout);
			};
		}
	}, [getSystems2, popupTimeoutEnabled, hideRemoveSystemConfirmation]);

	const closeButtonHover = (e) => {
		const button = e.target;
		button.style.backgroundColor = 'rgba(0, 172, 193, 0.1)';
	};

	const closeButtonLeave = (e) => {
		const button = e.target;
		button.style.backgroundColor = '#ffffff';
	};

	const handleDeleteSystem = () => {
		deleteSystem(system.ID);
	};

	const handleCloseNotification = () => {
		setIsLoaderRender(false);
		closeDeleteSystemNotification();
	};

	const renderDesktopNotifications = (): JSX.Element => {
		if (mobileView) return null;

		const color = deleteSystemSuccessfully ? 'success' : 'danger';
		const message = deleteSystemSuccessfully
			? 'System was deleted successfully.'
			: 'You do not have the permissions to remove the system or there are still other users in the system.';
		const isOpen = deleteSystemSuccessfully || deleteSystemFailed ? true : false;
		return (
			<Snackbar
				close={true}
				closeNotification={handleCloseNotification}
				color={color}
				message={message}
				open={isOpen}
				place='tc'
			/>
		);
	};

	const renderMobileNotifications = () => {
		const headerStyle = utils.getHeaderStyle(deleteSystemFailed);
		if (!mobileView) return null;
		if (deleteSystemFailed || deleteSystemSuccessfully) {
			return (
				<GridItem xs={12}>
					<h5
						className={deleteSystemFailed ? 'notification-open-create-system-fail' : 'notification-open-create-system-success'}
						style={headerStyle}
					>
						{deleteSystemFailed
							? 'You do not have the permissions to remove the system or there are still other users in the system.'
							: 'System deleted successfully.'}
					</h5>
				</GridItem>
			);
		}
	};

	const handleRemoveButtonClicked = () => {
		setIsLoaderRender(true);
		handleDeleteSystem();
	};

	return (
		<div>
			<GridContainer>
				<GridItem xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<Button
						data-testid='cancelButton'
						simple
						justIcon
						color='info'
						style={closeSweetAlertStyle}
						onClick={hideRemoveSystemConfirmation}
					>
						<CancelIcon style={{ width: '20px', height: '20px' }} />
					</Button>
				</GridItem>
			</GridContainer>
			<Card chart style={{ overflow: 'visible', marginTop: '10px', marginBottom: '0' }} className='node-edit-card'>
				<CardHeader color='info' icon>
					<CardIcon color='info'>
						<SystemIcon />
					</CardIcon>
					<h3 style={utils.sweetAlertHeaderStyle} className='remove-system-header'>
						{`${system.name}`}
					</h3>
				</CardHeader>
				<CardBody>
					<GridContainer>
						<GridItem xs={12}>
							<h5>This operation is irreversible.<br />Are you sure you want to delete this system?</h5>
						</GridItem>
						{renderMobileNotifications()}
						<GridItem xs={12} style={{ margin: '15px 0 0 0 ' }}>
							<div style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
								<Button
									className='deleteSystemCloseButton'
									simple
									color='info'
									style={closeButtonStyle}
									onClick={hideRemoveSystemConfirmation}
									onMouseEnter={(e) => closeButtonHover(e)}
									onMouseLeave={(e) => closeButtonLeave(e)}
								>
									cancel
								</Button>
								<Button
									style={{ backgroundColor: isLoaderRender ? '#ccc' : '#f44336' }}
									onClick={handleRemoveButtonClicked}
									disabled={isLoaderRender}
								>
									{isLoaderRender ? <Loading transform={1} height={17} width={48} color='darkGray' /> : 'remove'}
								</Button>
							</div>
						</GridItem>
					</GridContainer>
				</CardBody>
			</Card>
			{renderDesktopNotifications()}
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		systems: state.dashboard.systems,
		deleteSystemSuccessfully: state.installer.deleteSystemSuccessfully,
		deleteSystemFailed: state.installer.deleteSystemFailed,
		mobileView: state.dashboard.mobileView,
	};
};

const actions = {
	deleteSystem,
	closeDeleteSystemNotification,
	getSystems2,
};

export default connect(mapStateToProps, actions)(RemoveSystemConfirmationCard);
